import React, { useState, useContext, useCallback, useEffect } from 'react';
import { faPaperPlane, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Label, FormGroup, Card, CardBody, Spinner } from "reactstrap";
import AsyncSelect from 'react-select/async';
import { PostData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import './AIAssistant.css';

const AIAssistantTraining = () => {
    const { currentWebsiteId } = useContext(ProductContext);
    const navigate = useNavigate();
    const { trainingId } = useParams(); // Get trainingId from URL for edit mode
    const isEditMode = !!trainingId;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        selectedSkus: [],
        question: '',
        answer: '',
        type: 'correction',
        confidence: 'high'
    });

    // Dropdown options
    const typeOptions = [
        { value: 'correction', label: 'Correction' },
        { value: 'restriction', label: 'Restriction' },
        { value: 'product_spec', label: 'Product Spec' },
        { value: 'policy', label: 'Policy' },
        { value: 'suggestion', label: 'Suggestion' }
    ];

    const confidenceOptions = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
    ];
    const [errors, setErrors] = useState({});

    // Load existing training data when in edit mode
    useEffect(() => {
        const fetchTrainingData = async () => {
            if (!isEditMode || !trainingId) return;

            const websiteId = currentWebsiteId || Cookies.get("_website_id");
            if (!websiteId) return;

            setIsLoading(true);
            try {
                const response = await PostData("ms1", "api/v1/aiassistanttraining/list", {
                    websiteId: websiteId,
                    page: 1,
                    limit: 1000,
                    searchTerm: ''
                });

                if (response !== "Invalid" && response !== undefined && response.status === "success") {
                    const trainings = response.data.trainings || [];
                    // Use string comparison to handle BigInt/number differences
                    const training = trainings.find(t => String(t.trainingId) === String(trainingId));
                    
                    if (training) {
                        // Parse SKUs and create select options
                        const skuArray = training.sku ? training.sku.split(',').map(s => s.trim()) : [];
                        const skuOptions = skuArray.map(sku => ({
                            value: sku,
                            label: sku
                        }));

                        setFormData({
                            selectedSkus: skuOptions,
                            question: training.question || '',
                            answer: training.answer || '',
                            type: training.type || 'correction',
                            confidence: training.confidence || 'high'
                        });
                    } else {
                        toast.error('Training not found');
                        navigate('/aiassistant/training');
                    }
                } else {
                    toast.error('Failed to load training data');
                    navigate('/aiassistant/training');
                }
            } catch (error) {
                console.error('Error fetching training data:', error);
                toast.error('Error loading training data');
                navigate('/aiassistant/training');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTrainingData();
    }, [isEditMode, trainingId, currentWebsiteId, navigate]);

    // Async load products based on search input
    const loadProductOptions = useCallback(async (inputValue) => {
        const websiteId = currentWebsiteId || Cookies.get("_website_id");

        if (!websiteId) {
            console.error("No website ID found");
            return [];
        }

        try {
            const postData = {
                page: 1,
                limit: 50,
                searchTerm: inputValue || '',
                websiteId
            };

            const response = await PostData("ms1", "api/v1/aiassistant/products", postData);

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    const products = response.data.products || [];
                    // Map products to react-select options format
                    const options = products.map(product => ({
                        value: product.sku,
                        label: `${product.sku} - ${product.name || 'No Name'}`
                    }));
                    // Remove duplicates based on SKU value
                    const uniqueOptions = options.filter((option, index, self) =>
                        index === self.findIndex(o => o.value === option.value)
                    );
                    return uniqueOptions;
                }
            }
            return [];
        } catch (error) {
            console.error("Error fetching products:", error);
            return [];
        }
    }, [currentWebsiteId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSkuChange = (selectedOptions) => {
        setFormData(prev => ({
            ...prev,
            selectedSkus: selectedOptions || []
        }));

        // Clear error when user selects SKUs
        if (errors.sku) {
            setErrors(prev => ({
                ...prev,
                sku: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.selectedSkus || formData.selectedSkus.length === 0) {
            newErrors.sku = 'At least one SKU is required';
        }

        if (!formData.answer.trim()) {
            newErrors.answer = 'Answer is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        const websiteId = currentWebsiteId || Cookies.get("_website_id");
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        setIsSubmitting(true);
        try {
            // Extract SKU values from selected options
            const skus = formData.selectedSkus.map(option => option.value);

            const payload = {
                websiteId: websiteId,
                skus: skus,
                question: formData.question.trim(),
                answer: formData.answer.trim(),
                type: formData.type,
                confidence: formData.confidence
            };

            let response;
            if (isEditMode) {
                // Update existing training
                payload.trainingId = parseInt(trainingId);
                response = await PostData("ms1", "api/v1/aiassistanttraining/updateAiAssistantTrainingQuestionAnswer", payload);
            } else {
                // Create new training
                response = await PostData("ms1", "api/v1/aiassistanttraining/createAiAssistantTrainingQuestionAnswer", payload);
            }

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    toast.success(response.message || (isEditMode ? 'Training updated successfully!' : 'Training added successfully!'));
                    // Clear form
                    setFormData({
                        selectedSkus: [],
                        question: '',
                        answer: '',
                        type: 'correction',
                        confidence: 'high'
                    });
                    setErrors({});
                    // Navigate back to list
                    navigate('/aiassistant/training');
                } else {
                    toast.error(response.message?.message || response.message || (isEditMode ? "Failed to update training" : "Failed to add training data"));
                }
            } else {
                toast.error("Invalid response from server");
            }
        } catch (error) {
            console.error('Error submitting training data:', error);
            toast.error('Error submitting training data. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate('/aiassistant/training');
    };

    // Custom styles for react-select
    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '40px',
            borderColor: errors.sku ? '#dc3545' : state.isFocused ? '#005EFF' : '#ddd',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 94, 255, 0.2)' : 'none',
            '&:hover': {
                borderColor: errors.sku ? '#dc3545' : '#005EFF'
            }
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e9ecef',
            borderRadius: '4px'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#333',
            fontSize: '13px'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#6c757d',
            '&:hover': {
                backgroundColor: '#dc3545',
                color: 'white'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#005EFF' : state.isFocused ? '#f8f9fa' : 'white',
            color: state.isSelected ? 'white' : '#333',
            '&:hover': {
                backgroundColor: state.isSelected ? '#005EFF' : '#f8f9fa'
            }
        })
    };

    if (isLoading) {
        return (
            <div className="ai-assistant-training d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="ai-assistant-training">
                {/* Page Header */}
                <div className="mb-4">
                    <Button
                        color="link"
                        className="p-0 mb-2 text-muted"
                        onClick={handleBack}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to Training List
                    </Button>
                    <h2 className="mb-1">{isEditMode ? 'Edit AI Training' : 'Add AI Training'}</h2>
                    <p className="text-muted mb-0">
                        {isEditMode ? 'Update your AI training data' : 'Train your AI assistant with product-specific answers'}
                    </p>
                </div>

                {/* Training Form */}
                <Card style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
                    <CardBody className="p-4">
                        <FormGroup className="mb-3">
                            <Label for="sku" className="form-label fw-bold">
                                SKU(s) <span className="text-danger">*</span>
                            </Label>
                            <AsyncSelect
                                isMulti
                                id="sku"
                                name="sku"
                                value={formData.selectedSkus}
                                onChange={handleSkuChange}
                                loadOptions={loadProductOptions}
                                defaultOptions
                                cacheOptions
                                placeholder="Type to search products..."
                                noOptionsMessage={({ inputValue }) => 
                                    inputValue ? "No products found" : "Type to search..."
                                }
                                loadingMessage={() => "Searching products..."}
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                            {errors.sku && (
                                <div className="invalid-feedback d-block">
                                    {errors.sku}
                                </div>
                            )}
                            <small className="text-muted">
                                Select multiple SKUs to apply the same training to all of them
                            </small>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="question" className="form-label fw-bold">
                                Question
                            </Label>
                            <Input
                                type="text"
                                id="question"
                                name="question"
                                value={formData.question}
                                onChange={handleInputChange}
                                placeholder="Enter the question"
                                className={errors.question ? 'is-invalid' : ''}
                            />
                            {errors.question && (
                                <div className="invalid-feedback d-block">
                                    {errors.question}
                                </div>
                            )}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="answer" className="form-label fw-bold">
                                Answer <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="textarea"
                                id="answer"
                                name="answer"
                                value={formData.answer}
                                onChange={handleInputChange}
                                placeholder="Enter the training answer for this product"
                                rows={4}
                                style={{ resize: 'vertical' }}
                                className={errors.answer ? 'is-invalid' : ''}
                            />
                            {errors.answer && (
                                <div className="invalid-feedback d-block">
                                    {errors.answer}
                                </div>
                            )}
                        </FormGroup>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup className="mb-3">
                                    <Label for="type" className="form-label fw-bold">
                                        Type
                                    </Label>
                                    <Input
                                        type="select"
                                        id="type"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                    >
                                        {typeOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup className="mb-3">
                                    <Label for="confidence" className="form-label fw-bold">
                                        Confidence
                                    </Label>
                                    <Input
                                        type="select"
                                        id="confidence"
                                        name="confidence"
                                        value={formData.confidence}
                                        onChange={handleInputChange}
                                    >
                                        {confidenceOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                color="secondary"
                                outline
                                onClick={handleBack}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="d-flex align-items-center conversionBox"
                            >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                                {isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update' : 'Submit')}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </React.Fragment>
    );
};

export default AIAssistantTraining;
